/**
 * Modal for closing POS cash boxes
 * Allows staff to enter counted amounts and close cash boxes
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Input, Button, Space, Alert, Spin, Table, Typography } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useBoxesToClose, useCloseCashBoxes } from '../api/posClosureApi';
import type { PosBoxClosureItem, PosBoxClosureResult } from '../types/closure';
import { getFormattedPrice } from '@tools/utils/common.tsx';
import { useAppSelector } from '@store/store.tsx';
import { selectSiteApps } from '@store/slices/settingsSlice.tsx';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface PosClosureModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  closures: Record<number, number>;
  notes?: string;
}

export const PosClosureModal: React.FC<PosClosureModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<FormValues>();
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<PosBoxClosureResult[]>([]);
  const siteApps = useAppSelector(selectSiteApps) ?? [];
  const cashSessionEnabled = siteApps.includes('erp__cash_session');

  const { data: boxes, isLoading: loadingBoxes, error: boxesError } = useBoxesToClose({
    enabled: open && cashSessionEnabled,
  });
  // const { data: tolerance, isLoading: loadingTolerance } = useTolerance();
  const closeMutation = useCloseCashBoxes();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setShowResults(false);
      setResults([]);
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async (values: FormValues) => {
    if (!boxes) return;

    // Convert form values to API format
    const closureItems: PosBoxClosureItem[] = boxes
      .map(box => ({
        box_id: box.id,
        counted_amount: values.closures?.[box.id] ?? 0,
      }))
      .filter(item => item.counted_amount !== undefined);

    try {
      const response = await closeMutation.mutateAsync({
        closures: closureItems,
        notes: values.notes || null,
      });

      setResults(response.results);
      setShowResults(true);

      // If all successful, auto-close after a delay
      if (response.overall_status === 'completed') {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error('Error closing cash boxes:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />;
      case 'exceeds_tolerance':
        return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 20 }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
      default:
        return <ExclamationCircleOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
    }
  };

  const resultsColumns = [
    {
      title: 'Caja',
      dataIndex: 'box_name',
      key: 'box_name',
    },
    {
      title: 'Monto Ingresado',
      dataIndex: 'counted_amount',
      key: 'counted_amount',
      render: (amount: number) => getFormattedPrice(amount),
    },
    {
      title: 'Monto Esperado',
      dataIndex: 'expected_amount',
      key: 'expected_amount',
      render: (amount: number | null | undefined) =>
        amount !== null && amount !== undefined ? getFormattedPrice(amount) : '-',
    },
    {
      title: 'Diferencia',
      dataIndex: 'difference_amount',
      key: 'difference_amount',
      render: (diff: number | null | undefined) => {
        if (diff === null || diff === undefined) return '-';
        const color = Math.abs(diff) < 0.01 ? '#52c41a' : diff > 0 ? '#1890ff' : '#ff4d4f';
        return <Text style={{ color }}>{getFormattedPrice(diff)}</Text>;
      },
    },
    {
      title: 'Estado',
      key: 'status',
      render: (_: any, record: PosBoxClosureResult) => (
        <Space>
          {getStatusIcon(record.status)}
          <Text>{record.message}</Text>
        </Space>
      ),
    },
  ];

  if (!open) return null;

  return (
    <Modal
      title="Cierre de Caja"
      open={open}
      onCancel={onClose}
      width={800}
      footer={
        showResults
          ? [
              <Button key="close" onClick={onClose}>
                Cerrar
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Cancelar
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={() => form.submit()}
                loading={closeMutation.isPending}
                disabled={!boxes || boxes.length === 0}
              >
                Cerrar Cajas
              </Button>,
            ]
      }
    >
      {!cashSessionEnabled ? (
        <Alert
          title="Cierre de caja no habilitado"
          description="La aplicación de sesiones de caja no está habilitada para este sitio."
          type="info"
        />
      ) : loadingBoxes ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : boxesError ? (
        <Alert title="Error al cargar cajas" description="No se pudieron cargar las cajas a cerrar" type="error" />
      ) : showResults ? (
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <Alert
            title={
              results.every(r => r.status === 'success')
                ? 'Cierre completado exitosamente'
                : results.some(r => r.status === 'success')
                  ? 'Cierre parcial'
                  : 'Error en el cierre'
            }
            type={
              results.every(r => r.status === 'success')
                ? 'success'
                : results.some(r => r.status === 'success')
                  ? 'warning'
                  : 'error'
            }
          />
          <Table
            dataSource={results}
            columns={resultsColumns}
            rowKey="box_id"
            pagination={false}
            size="small"
          />
        </Space>
      ) : !boxes || boxes.length === 0 ? (
        <Alert title="No hay cajas para cerrar" description="No se encontraron cajas que requieran cierre" type="info" />
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/*{tolerance && (*/}
          {/*  <Alert*/}
          {/*    title={`Tolerancia configurada: ${getFormattedPrice(tolerance.amount)}`}*/}
          {/*    description="Las diferencias dentro de este rango serán ajustadas automáticamente"*/}
          {/*    type="info"*/}
          {/*    showIcon*/}
          {/*    style={{ marginBottom: 16 }}*/}
          {/*  />*/}
          {/*)}*/}

          <Title level={5}>Ingrese el monto contado en cada caja:</Title>

          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            {boxes.map(box => (
              <Form.Item
                key={box.id}
                label={box.name}
                name={['closures', box.id]}
                rules={[
                  { required: true, message: 'Ingrese el monto contado' },
                  { type: 'number', min: 0, message: 'El monto debe ser mayor o igual a 0' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  precision={2}
                  min={0}
                  prefix="$"
                />
              </Form.Item>
            ))}
          </Space>

          <Form.Item label="Notas (opcional)" name="notes">
            <TextArea rows={3} placeholder="Agregar notas sobre el cierre..." maxLength={500} showCount />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
